import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, User, ArrowUpRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiService from '../api/aiService';

const ChatAssistant = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([
        { id: 1, text: "Hello! I'm your Xpanse AI Assistant. How can I help you today?", sender: 'bot' }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSend = async (e) => {
        e.preventDefault();
        const currentMsg = message.trim();
        if (!currentMsg) return;

        const newUserMessage = { id: Date.now(), text: currentMsg, sender: 'user' };
        setMessages(prev => [...prev, newUserMessage]);
        setMessage('');
        setIsTyping(true);

        try {
            const responseText = await aiService.sendMessage(currentMsg);
            const botResponse = { 
                id: Date.now() + 1, 
                text: responseText, 
                sender: 'bot' 
            };
            setMessages(prev => [...prev, botResponse]);
        } catch (error) {
            console.log("Error in Chat Assistant :", error);
            
            const errorResponse = { 
                id: Date.now() + 1, 
                text: "Sorry, I'm having trouble connecting. Please try again later.", 
                sender: 'bot'
            };
            setMessages(prev => [...prev, errorResponse]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="chat-assistant-container">
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="chat-window"
                    >
                        <div className="chat-header">
                            <div className="header-info">
                                <div className="bot-avatar">
                                    <Bot size={20} />
                                </div>
                                <div className="header-text">
                                    <h6>Xpanse AI</h6>
                                    <span>Online & Ready to help</span>
                                </div>
                            </div>
                            <div className="header-actions" onClick={() => setIsOpen(false)}>
                                <X size={20} />
                            </div>
                        </div>

                        <div className="chat-messages">
                            {messages.map((msg) => (
                                <div key={msg.id} className={`message ${msg.sender}`}>
                                    {msg.sender === 'bot' ? (
                                        <div className="markdown-content">
                                            <ReactMarkdown 
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    a: ({ href, children, ...props }) => {
                                                        const isInternal = href && href.startsWith('/');
                                                        if (isInternal) {
                                                            return (
                                                                <a 
                                                                    href={href} 
                                                                    className="chat-nav-link"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        navigate(href);
                                                                    }}
                                                                    {...props}
                                                                >
                                                                    {children}
                                                                    <ArrowUpRight size={14} style={{ marginLeft: '4px', flexShrink: 0 }} />
                                                                </a>
                                                            );
                                                        }
                                                        return (
                                                            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                                                {children}
                                                            </a>
                                                        );
                                                    }
                                                }}
                                            >
                                                {msg.text}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        msg.text
                                    )}
                                </div>
                            ))}
                            {isTyping && (
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form className="chat-input-area" onSubmit={handleSend}>
                            <input 
                                type="text" 
                                placeholder="Type your message..." 
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                            <button type="submit" disabled={!message.trim() || isTyping}>
                                <Send size={18} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="chat-bubble"
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? <X size={28} /> : <MessageSquare size={28} />}
            </motion.div>
        </div>
    );
};

export default ChatAssistant;
